import axios from 'axios';
import { useEffect, useState } from 'react';
import Box from 'elements/Box';
import { Button, TextField, Select, MenuItem, FormControl, InputLabel, Grid, Switch, FormControlLabel, Dialog, InputAdornment, IconButton } from '@mui/material';
import { ProjectConfig } from 'Global';
import { useSnackbar } from 'notistack';
import { ApiRequest } from 'GlobalFunctions';
import ISwitch from "elements/ISwitch";
import { PiEyeLight } from "react-icons/pi";
import { PiEyeSlashLight } from "react-icons/pi";

export default function Configuration(props: any) {
  const { enqueueSnackbar } = useSnackbar();
  const { setLoading } = props;
  
  const defaultValues: any = {
    quote_note_1: "",
    quote_note_2: "",
    quote_validity: "",
    quote_validity_type: "dias",
    order_note_1: "",
    order_note_2: "",
    minimum_order_enabled: false,
    minimum_order_value: "",
    allow_client_updates: false,
    photo_required_orders: false,
    photo_required_undelivery: false,
    // Abastecimiento
    supply_base_retencion: "",
    supply_retefuente: "",
    supply_ica: "",
    // Advanced configuration (requires master password)
    max_discount: 0,
    enableGeolocation: false,
    geolocationOrders: false,
    geolocationOrdersPrecision: 0,
    geolocationRoutes: false,
    geolocationRoutesPrecision: 0,
    allow_route_editing: false,
    picking_show_prices: false,
    picking_certification: false,
  };

  const [formValues, setFormValues] = useState(defaultValues);
  const [initialAdvancedValues, setInitialAdvancedValues] = useState<any>({});

  // Master password dialog
  const [openPasswordDialog, setOpenPasswordDialog] = useState(false);
  const [masterPassword, setMasterPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);

  // Warehouses state
  const [warehouses, setWarehouses] = useState([]);

  // Safety alerts config
  const [safetyConfig, setSafetyConfig] = useState<any>({
    interval_minutes: 60,
    is_active: false,
  });

  // Initialize warehouse dialog state
  const [openInitDialog, setOpenInitDialog] = useState(false);
  const [selectedWarehouse, setSelectedWarehouse] = useState<any>(null);
  const [initStock, setInitStock] = useState<number>(0);
  const [warehousePassword, setWarehousePassword] = useState("");
  const [showWarehousePassword, setShowWarehousePassword] = useState(false);
  
  useEffect(() => {
    setLoading(false);
  }, []);
  
  useEffect(() => {
    loadConfiguration();
    loadWarehouses();
    loadSafetyConfig();
  }, []);

  const loadWarehouses = () => {
    ApiRequest({
      url: '/warehouses/list',
      method: 'get',
      headers: {
        ltkn: localStorage.getItem('ltkn')
      },
      params: {
        full_list: true,
      },
      setResponse: (response: any) => {
        setWarehouses(response.list);
      },
      error: (err: any) => {
        console.log(err);
      }
    });
  };

  const loadSafetyConfig = () => {
    ApiRequest({
      url: '/safety-alerts/config',
      method: 'get',
      headers: { ltkn: localStorage.getItem('ltkn') },
      setResponse: (response: any) => {
        if (response.config_id) {
          setSafetyConfig({
            interval_minutes: response.interval_minutes || 60,
            is_active: response.is_active === 1 || response.is_active === true,
          });
        }
      },
    });
  };

  const saveSafetyConfig = () => {
    ApiRequest({
      url: '/safety-alerts/config',
      method: 'post',
      headers: { ltkn: localStorage.getItem('ltkn') },
      body: safetyConfig,
      setResponse: (response: any) => {
        if (response.status === 'success') {
          enqueueSnackbar('Configuración de alertas guardada', { variant: 'success' });
        } else {
          enqueueSnackbar(response.message || 'Error', { variant: 'error' });
        }
      },
    });
  };

  const loadConfiguration = () => {
    setLoading(true);
    ApiRequest({
      url: '/companies/getConfiguration',
      method: 'get',
      headers: {
        ltkn: localStorage.getItem('ltkn')
      },
      setResponse: (response: any) => {
        console.log("Configuration:", response);
        // Ensure quote_validity_type has a default value if not present
        if (!response.quote_validity_type) {
          response.quote_validity_type = 'dias';
        }
        setFormValues(response);

        // Save initial advanced values to detect changes
        setInitialAdvancedValues({
          max_discount: response.max_discount || 0,
          enableGeolocation: response.enableGeolocation || false,
          geolocationOrders: response.geolocationOrders || false,
          geolocationOrdersPrecision: response.geolocationOrdersPrecision || 0,
          geolocationRoutes: response.geolocationRoutes || false,
          geolocationRoutesPrecision: response.geolocationRoutesPrecision || 0,
          allow_route_editing: response.allow_route_editing || false,
          picking_show_prices: response.picking_show_prices || false,
          picking_certification: response.picking_certification || false,
        });

        setLoading(false);
      },
      error: (err: any) => {
        console.log(err);
        enqueueSnackbar('Error al cargar la configuración', { variant: 'error' });
        setLoading(false);
      }
    });
  };
  
  const handleInputChange = (e: { target: { name: any; value: any; }; }) => {
    const { name, value } = e.target;
    setFormValues({
      ...formValues,
      [name]: value,
    });
  };
  
  const handleSwitchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, checked } = e.target;
    setFormValues({
      ...formValues,
      [name]: checked,
    });
  };

  // Check if advanced configuration has changed
  const hasAdvancedConfigChanged = () => {
    return (
      formValues.max_discount !== initialAdvancedValues.max_discount ||
      formValues.enableGeolocation !== initialAdvancedValues.enableGeolocation ||
      formValues.geolocationOrders !== initialAdvancedValues.geolocationOrders ||
      formValues.geolocationOrdersPrecision !== initialAdvancedValues.geolocationOrdersPrecision ||
      formValues.geolocationRoutes !== initialAdvancedValues.geolocationRoutes ||
      formValues.geolocationRoutesPrecision !== initialAdvancedValues.geolocationRoutesPrecision ||
      formValues.allow_route_editing !== initialAdvancedValues.allow_route_editing ||
      formValues.picking_show_prices !== initialAdvancedValues.picking_show_prices ||
      formValues.picking_certification !== initialAdvancedValues.picking_certification
    );
  };

  const handleSubmit = () => {
    // If advanced config changed, require master password
    if (hasAdvancedConfigChanged()) {
      setOpenPasswordDialog(true);
    } else {
      // Save directly without password validation
      saveConfiguration();
    }
  };

  const saveConfiguration = (password?: string) => {
    setLoading(true);

    ApiRequest({
      url: '/companies/updateConfiguration',
      method: 'post',
      headers: {
        ltkn: localStorage.getItem('ltkn'),
      },
      body: {
        configuration: formValues,
        master_password: password || null,
      },
      setResponse: (response: any) => {
        if (response.status === "success") {
          enqueueSnackbar('Configuración guardada con éxito', { variant: 'success' });
          // Update initial advanced values
          setInitialAdvancedValues({
            max_discount: formValues.max_discount,
            max_discount_type: formValues.max_discount_type,
            enableGeolocation: formValues.enableGeolocation,
            geolocationOrders: formValues.geolocationOrders,
            geolocationOrdersPrecision: formValues.geolocationOrdersPrecision,
            geolocationRoutes: formValues.geolocationRoutes,
            geolocationRoutesPrecision: formValues.geolocationRoutesPrecision,
            allow_route_editing: formValues.allow_route_editing,
            picking_show_prices: formValues.picking_show_prices,
            picking_certification: formValues.picking_certification,
          });
          setOpenPasswordDialog(false);
          setMasterPassword("");
        } else {
          enqueueSnackbar(response.message, { variant: 'error' });
        }
        setLoading(false);
      },
      error: (err: any) => {
        console.log(err);
        enqueueSnackbar('Error al guardar la configuración', { variant: 'error' });
        setLoading(false);
      }
    });
  };

  /*=======================================
  INITIALIZE WAREHOUSE
  =======================================*/
  const handleInitializeWarehouse = () => {
    if (!selectedWarehouse || !warehousePassword) {
      enqueueSnackbar('Por favor ingrese la contraseña maestra', { variant: 'error' });
      return;
    }

    setLoading(true);
    ApiRequest({
      url: '/products/initializeWarehouseInventory',
      method: 'post',
      headers: {
        ltkn: localStorage.getItem('ltkn')
      },
      body: {
        warehouse_id: selectedWarehouse.warehouse_id,
        stock: Number(initStock),
        master_password: warehousePassword,
      },
      setResponse: (response: any) => {
        if (response.status === "success") {
          enqueueSnackbar(`Inventario de ${selectedWarehouse.warehouse_name} inicializado correctamente`, { variant: "success" });
          setOpenInitDialog(false);
          setSelectedWarehouse(null);
          setInitStock(0);
          setWarehousePassword("");
          setShowWarehousePassword(false);
        } else {
          enqueueSnackbar(response.message, { variant: "error" });
        }
        setLoading(false);
      },
      error: (err: any) => {
        console.log(err);
        enqueueSnackbar("Error al inicializar el inventario", { variant: "error" });
        setLoading(false);
      }
    });
  };

  return (
    <div id='Configuration' className='ModuleContainer'>
      <div className="ModuleWrapper">
        <div className="boxContainerHeader">
          <div className="boxContainerTitle">
            <h2 className="h2_title">Configuración</h2>
            <span className="h2_subtitle">Configure los parámetros de su empresa</span>
          </div>
        </div>
        
        <Box className='box100'>
          <div id="configurationForm">
            <div className='input_group'>
              <h2>Cotizaciones</h2>
              
              <TextField
                id="quote_note_1"
                name="quote_note_1"
                label="Nota de Cotización 1"
                type="text"
                multiline
                rows={3}
                fullWidth
                value={formValues.quote_note_1}
                onChange={handleInputChange}
                margin="normal"
              />
              
              <TextField
                id="quote_note_2"
                name="quote_note_2"
                label="Nota de Cotización 2"
                type="text"
                multiline
                rows={3}
                fullWidth
                value={formValues.quote_note_2}
                onChange={handleInputChange}
                margin="normal"
              />
              
              <Grid container spacing={2} style={{ marginTop: '8px' }}>
                <Grid item xs={6}>
                  <TextField
                    id="quote_validity"
                    name="quote_validity"
                    label="Vigencia de Cotización"
                    type="number"
                    fullWidth
                    value={formValues.quote_validity}
                    onChange={handleInputChange}
                    InputProps={{
                      inputProps: { 
                        min: 0
                      }
                    }}
                  />
                </Grid>
                <Grid item xs={6}>
                  <FormControl fullWidth>
                    <InputLabel id="quote-validity-type-label">Tipo de Vigencia</InputLabel>
                    <Select
                      labelId="quote-validity-type-label"
                      id="quote_validity_type"
                      name="quote_validity_type"
                      value={formValues.quote_validity_type}
                      label="Tipo de Vigencia"
                      onChange={handleInputChange}
                    >
                      <MenuItem value="horas">Horas</MenuItem>
                      <MenuItem value="dias">Días</MenuItem>
                      <MenuItem value="semanas">Semanas</MenuItem>
                      <MenuItem value="meses">Meses</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
              </Grid>
            </div>
            
            <div className='input_group' style={{ marginTop: '20px' }}>
              <h2>Pedidos</h2>
              
              <TextField
                id="order_note_1"
                name="order_note_1"
                label="Nota de Pedido 1"
                type="text"
                multiline
                rows={3}
                fullWidth
                value={formValues.order_note_1}
                onChange={handleInputChange}
                margin="normal"
              />
              
              <TextField
                id="order_note_2"
                name="order_note_2"
                label="Nota de Pedido 2"
                type="text"
                multiline
                rows={3}
                fullWidth
                value={formValues.order_note_2}
                onChange={handleInputChange}
                margin="normal"
              />
              
              <FormControlLabel
                control={
                  <Switch
                    checked={formValues.minimum_order_enabled}
                    onChange={handleSwitchChange}
                    name="minimum_order_enabled"
                    color="primary"
                  />
                }
                label="¿Pedido mínimo?"
                style={{ marginTop: '16px' }}
              />
              
              {formValues.minimum_order_enabled && (
                <TextField
                  id="minimum_order_value"
                  name="minimum_order_value"
                  label="Valor del pedido mínimo"
                  type="number"
                  fullWidth
                  value={formValues.minimum_order_value}
                  onChange={handleInputChange}
                  margin="normal"
                  InputProps={{
                    inputProps: { 
                      min: 0
                    }
                  }}
                />
              )}
            </div>
            
            <div className='input_group' style={{ marginTop: '20px' }}>
              <h2>Permisos</h2>
              
              <FormControlLabel
                control={
                  <Switch
                    checked={formValues.allow_client_updates}
                    onChange={handleSwitchChange}
                    name="allow_client_updates"
                    color="primary"
                  />
                }
                label="Permitir actualización de clientes"
                style={{ marginTop: '16px' }}
              />
            </div>

            <div className='input_group' style={{ marginTop: '20px' }}>
              <h2>Abastecimiento</h2>

              <TextField
                id="supply_base_retencion"
                name="supply_base_retencion"
                label="Base retención"
                type="number"
                fullWidth
                value={formValues.supply_base_retencion ?? ""}
                onChange={handleInputChange}
                margin="normal"
                InputProps={{
                  inputProps: {
                    min: 0,
                    step: 0.01,
                  },
                }}
                helperText="Valor numérico con hasta 2 decimales"
              />

              <Grid container spacing={2} style={{ marginTop: '8px' }}>
                <Grid item xs={6}>
                  <TextField
                    id="supply_retefuente"
                    name="supply_retefuente"
                    label="Retefuente (%)"
                    type="number"
                    fullWidth
                    value={formValues.supply_retefuente ?? ""}
                    onChange={handleInputChange}
                    InputProps={{
                      inputProps: {
                        min: 0,
                        step: 0.01,
                      },
                      endAdornment: <InputAdornment position="end">%</InputAdornment>,
                    }}
                  />
                </Grid>
                <Grid item xs={6}>
                  <TextField
                    id="supply_ica"
                    name="supply_ica"
                    label="ICA (%)"
                    type="number"
                    fullWidth
                    value={formValues.supply_ica ?? ""}
                    onChange={handleInputChange}
                    InputProps={{
                      inputProps: {
                        min: 0,
                        step: 0.01,
                      },
                      endAdornment: <InputAdornment position="end">%</InputAdornment>,
                    }}
                  />
                </Grid>
              </Grid>
            </div>

            <div className='input_group' style={{ marginTop: '20px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '20px' }}>
                <h2>Foto Obligatoria:</h2>
                <div style={{ display: 'flex', alignItems: 'center', gap: '20px' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <span style={{ fontSize: '14px', fontWeight: 500 }}>Pedidos:</span>
                    <ISwitch
                      label=""
                      checked={formValues.photo_required_orders ?? false}
                      onChange={(e: any) => {
                        setFormValues({
                          ...formValues,
                          photo_required_orders: e.target.checked,
                        });
                      }}
                    />
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <span style={{ fontSize: '14px', fontWeight: 500 }}>Visita sin compra:</span>
                    <ISwitch
                      label=""
                      checked={formValues.photo_required_undelivery ?? false}
                      onChange={(e: any) => {
                        setFormValues({
                          ...formValues,
                          photo_required_undelivery: e.target.checked,
                        });
                      }}
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* Safety Alerts Configuration */}
            <div className='input_group' style={{ marginTop: '20px', border: '2px solid #1976d2', borderRadius: '8px', padding: '20px', backgroundColor: '#e3f2fd' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '15px' }}>
                <h2 style={{ margin: 0 }}>Alertas de Seguridad</h2>
              </div>

              <ISwitch
                label="Activar alertas periódicas"
                checked={safetyConfig.is_active}
                onChange={(e: any) => setSafetyConfig({ ...safetyConfig, is_active: e.target.checked })}
              />

              {safetyConfig.is_active && (
                <TextField
                  label="Intervalo entre alertas (minutos)"
                  type="number"
                  fullWidth
                  value={safetyConfig.interval_minutes}
                  onChange={(e) => setSafetyConfig({ ...safetyConfig, interval_minutes: Number(e.target.value) })}
                  margin="normal"
                  InputProps={{ inputProps: { min: 1 } }}
                  helperText="Cada cuántos minutos se envía la alerta a los usuarios activos"
                />
              )}

              <div style={{ marginTop: '15px' }}>
                <Button variant="contained" size="small" onClick={saveSafetyConfig}>
                  Guardar Alertas
                </Button>
              </div>
            </div>

            {/* Advanced Configuration - Requires Master Password */}
            <div className='input_group' style={{ marginTop: '20px', border: '2px solid #ff9800', borderRadius: '8px', padding: '20px', backgroundColor: '#fff3e0' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '15px' }}>
                <h2 style={{ margin: 0 }}>Configuración Avanzada</h2>
                <span style={{ fontSize: '12px', color: '#ff9800', fontWeight: 'bold', backgroundColor: '#fff', padding: '4px 8px', borderRadius: '4px' }}>
                  Requiere Contraseña Maestra
                </span>
              </div>

              {/* Max Discount Configuration */}
              <h3>Descuentos</h3>
              <TextField
                id="max_discount"
                name="max_discount"
                label="Descuento Máximo Global (%)"
                type="number"
                fullWidth
                value={formValues.max_discount !== undefined ? formValues.max_discount : 0}
                onChange={handleInputChange}
                margin="normal"
                InputProps={{
                  inputProps: {
                    min: 0
                  }
                }}
                helperText="Este descuento se aplicará a todos los productos que no tengan un descuento máximo personalizado"
              />

              {/* Geolocation */}
              <div style={{ marginTop: '20px' }}>
                <h3>Geolocalización</h3>
                <ISwitch
                  label="Activar Geolocalización"
                  checked={formValues.enableGeolocation ?? false}
                  onChange={(e: any) => {
                    const checked = e.target.checked;
                    setFormValues({
                      ...formValues,
                      enableGeolocation: checked,
                      geolocationOrders: checked ? formValues.geolocationOrders : false,
                      geolocationRoutes: checked ? formValues.geolocationRoutes : false,
                      geolocationOrdersPrecision: checked ? formValues.geolocationOrdersPrecision : 0,
                      geolocationRoutesPrecision: checked ? formValues.geolocationRoutesPrecision : 0,
                    });
                  }}
                />

                {formValues.enableGeolocation && (
                  <div style={{ marginLeft: '20px', marginTop: '15px' }}>
                    <ISwitch
                      label="Pedidos Geolocalización"
                      checked={formValues.geolocationOrders !== undefined ? formValues.geolocationOrders : false}
                      onChange={(e: any) => {
                        const checked = e.target.checked;
                        setFormValues({
                          ...formValues,
                          geolocationOrders: checked,
                          geolocationOrdersPrecision: checked ? formValues.geolocationOrdersPrecision : 0,
                        });
                      }}
                    />

                    {formValues.geolocationOrders && (
                      <TextField
                        id="geolocationOrdersPrecision"
                        name="geolocationOrdersPrecision"
                        label="Precisión Pedidos Geolocalización (metros)"
                        type="number"
                        required
                        fullWidth
                        value={formValues.geolocationOrdersPrecision}
                        onChange={(e) => {
                          setFormValues({
                            ...formValues,
                            geolocationOrdersPrecision: Number(e.target.value),
                          });
                        }}
                        margin="normal"
                        InputProps={{
                          inputProps: {
                            min: 0
                          }
                        }}
                      />
                    )}

                    <ISwitch
                      label="Rutas Geolocalización"
                      checked={formValues.geolocationRoutes !== undefined ? formValues.geolocationRoutes : false}
                      onChange={(e: any) => {
                        const checked = e.target.checked;
                        setFormValues({
                          ...formValues,
                          geolocationRoutes: checked,
                          geolocationRoutesPrecision: checked ? formValues.geolocationRoutesPrecision : 0,
                        });
                      }}
                    />

                    {formValues.geolocationRoutes && (
                      <TextField
                        id="geolocationRoutesPrecision"
                        name="geolocationRoutesPrecision"
                        label="Precisión Rutas Geolocalización (metros)"
                        type="number"
                        required
                        fullWidth
                        value={formValues.geolocationRoutesPrecision}
                        onChange={(e) => {
                          setFormValues({
                            ...formValues,
                            geolocationRoutesPrecision: Number(e.target.value),
                          });
                        }}
                        margin="normal"
                        InputProps={{
                          inputProps: {
                            min: 0
                          }
                        }}
                      />
                    )}
                  </div>
                )}
              </div>

              {/* Route Editing */}
              <div style={{ marginTop: '20px' }}>
                <h3>Rutas</h3>
                <ISwitch
                  label="Permitir edición de rutas"
                  checked={formValues.allow_route_editing ?? false}
                  onChange={(e: any) => {
                    setFormValues({
                      ...formValues,
                      allow_route_editing: e.target.checked,
                    });
                  }}
                />
              </div>

              {/* Picking */}
              <div style={{ marginTop: '20px' }}>
                <h3>Picking</h3>
                <ISwitch
                  label="Picking con certificación"
                  checked={formValues.picking_certification ?? false}
                  onChange={(e: any) => {
                    setFormValues({
                      ...formValues,
                      picking_certification: e.target.checked,
                    });
                  }}
                />
                <ISwitch
                  label="Mostrar precios"
                  checked={formValues.picking_show_prices ?? false}
                  onChange={(e: any) => {
                    setFormValues({
                      ...formValues,
                      picking_show_prices: e.target.checked,
                    });
                  }}
                />
              </div>

              {/* Warehouses - Initialize Inventory */}
              <div style={{ marginTop: '30px' }}>
                <h3>Bodegas - Inicializar Inventario</h3>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '10px', marginTop: '15px' }}>
                  {warehouses.map((warehouse: any) => (
                    <div
                      key={warehouse.warehouse_id}
                      style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        padding: '10px',
                        border: '1px solid #e0e0e0',
                        borderRadius: '4px',
                      }}
                    >
                      <span style={{ fontSize: '14px', fontWeight: 500 }}>
                        {warehouse.warehouse_name}
                      </span>
                      <Button
                        variant="outlined"
                        size="small"
                        onClick={() => {
                          setSelectedWarehouse(warehouse);
                          setOpenInitDialog(true);
                        }}
                      >
                        Inicializar
                      </Button>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            <div className="formActions" style={{ marginTop: '20px' }}>
              <Button
                onClick={handleSubmit}
                variant="contained"
                color="primary"
              >
                Guardar Configuración
              </Button>
            </div>
          </div>
        </Box>
      </div>

      {/* Master Password Dialog */}
      <Dialog
        open={openPasswordDialog}
        onClose={() => {
          setOpenPasswordDialog(false);
          setMasterPassword("");
          setShowPassword(false);
        }}
      >
        <div className="DialogContainer" style={{maxWidth:'400px'}}>
          <div className="ModuleWrapper">
            <div className="boxContainerHeader">
              <div className="boxContainerTitle">
                <h2 className="h2_title">Verificación de Seguridad</h2>
                <span className="h2_subtitle">
                  Ingrese la contraseña maestra para guardar cambios en la configuración avanzada
                </span>
              </div>
            </div>

            <div id="userForm">
              <div className="userForm_right">
                <div className='input_group'>
                  <TextField
                    id="master_password"
                    name="master_password"
                    label="Contraseña Maestra"
                    type={showPassword ? "text" : "password"}
                    fullWidth
                    autoComplete="off"
                    value={masterPassword}
                    onChange={(e) => setMasterPassword(e.target.value)}
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton
                            onClick={() => setShowPassword(!showPassword)}
                            edge="end"
                            size="small"
                          >
                            {showPassword ? <PiEyeSlashLight size={20} /> : <PiEyeLight size={20} />}
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="dialogButtons">
          <Button
            onClick={() => {
              setOpenPasswordDialog(false);
              setMasterPassword("");
              setShowPassword(false);
            }}
            variant="outlined"
            className="button_2"
          >
            Cancelar
          </Button>

          <Button
            onClick={() => {
              if (!masterPassword) {
                enqueueSnackbar('Por favor ingrese la contraseña maestra', { variant: 'error' });
                return;
              }
              saveConfiguration(masterPassword);
            }}
            variant="contained"
            className="button_1"
          >
            Confirmar
          </Button>
        </div>
      </Dialog>

      {/* Initialize Warehouse Dialog */}
      <Dialog
        open={openInitDialog}
        onClose={() => {
          setOpenInitDialog(false);
          setSelectedWarehouse(null);
          setInitStock(0);
          setWarehousePassword("");
          setShowWarehousePassword(false);
        }}
      >
        <div className="DialogContainer" style={{maxWidth:'450px'}}>
          <div className="ModuleWrapper">
            <div className="boxContainerHeader">
              <div className="boxContainerTitle">
                <h2 className="h2_title">Inicializar Inventario</h2>
                <span className="h2_subtitle">
                  {selectedWarehouse?.warehouse_name}
                </span>
              </div>
            </div>

            <div id="userForm">
              <div className="userForm_right">
                <div className='input_group'>
                  <p style={{ marginBottom: '15px', color: '#666' }}>
                    Esta acción establecerá el stock de todos los productos de esta bodega al valor indicado.
                  </p>
                  <TextField
                    id="init_stock"
                    name="init_stock"
                    label="Cantidad de Stock"
                    type="number"
                    required
                    fullWidth
                    value={initStock}
                    onChange={(e) => setInitStock(Number(e.target.value))}
                    onFocus={(e) => e.target.select()}
                    InputProps={{
                      inputProps: {
                        min: 0
                      }
                    }}
                    margin="normal"
                  />

                  {/* Master Password Field */}
                  <TextField
                    id="warehouse_password"
                    name="warehouse_password"
                    label="Contraseña Maestra"
                    type={showWarehousePassword ? "text" : "password"}
                    fullWidth
                    required
                    autoComplete="off"
                    value={warehousePassword}
                    onChange={(e) => setWarehousePassword(e.target.value)}
                    margin="normal"
                    helperText="Ingrese la contraseña maestra para confirmar esta acción"
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton
                            onClick={() => setShowWarehousePassword(!showWarehousePassword)}
                            edge="end"
                            size="small"
                          >
                            {showWarehousePassword ? <PiEyeSlashLight size={20} /> : <PiEyeLight size={20} />}
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="dialogButtons">
          <Button
            onClick={() => {
              setOpenInitDialog(false);
              setSelectedWarehouse(null);
              setInitStock(0);
              setWarehousePassword("");
              setShowWarehousePassword(false);
            }}
            variant="outlined"
            className="button_2"
          >
            Cancelar
          </Button>

          <Button
            onClick={handleInitializeWarehouse}
            variant="contained"
            className="button_1"
          >
            Confirmar
          </Button>
        </div>
      </Dialog>
    </div>
  );
}
